import React, { useState, useMemo } from "react";
import { Search, Pencil, Trash2, ChevronLeft, ChevronRight, Plus } from "lucide-react";

//Modals
import ServicetypeAddModal from "Pages/Modals/HourlyFare/Create";
import ServicetypeUpdateModal from "Pages/Modals/HourlyFare/Update";
import ServicetypeDeleteDialog from "Pages/Modals/HourlyFare/Delete";

// ---- Dummy data ----
const INITIAL_DUMMY_DATA = [
  { id: "01", hours: "01", distance: 10, extraKm: 1, extraAmount: 1 },
  { id: "02", hours: "02", distance: 30, extraKm: 2, extraAmount: 2 },
  { id: "03", hours: "03", distance: 50, extraKm: 3, extraAmount: 3 },
  { id: "04", hours: "04", distance: 70, extraKm: 4, extraAmount: 4 },
  { id: "05", hours: "05", distance: 90, extraKm: 5, extraAmount: 5 },
];

const PAGE_SIZE = 5;
const EXPORT_ACTIONS = ["Copy", "Excel", "CSV", "PDF"];

// ---- Styles ----
const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#ffffff",
    padding: "24px",
    boxSizing: "border-box",
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: "100%",
    background: "#FFFFFF",
    border: "none",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  },
  header: {
    display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
    gap: "12px", padding: "20px 24px", borderBottom: "1px solid #F1F5F9",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  title: { margin: 0, fontSize: "18px", fontWeight: 600, color: "#111827" },
  addBtn: {
    display: "flex", alignItems: "center", gap: "6px", background: "#111827", color: "#fff",
    border: "none", borderRadius: "8px", padding: "8px 14px", fontSize: "14px", fontWeight: 500, cursor: "pointer",
  },
  breadcrumb: { fontSize: "14px", color: "#6B7280" },
  breadcrumbActive: { fontWeight: 500, color: "#111827" },
  toolbar: {
    display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
    gap: "16px", padding: "16px 24px",
  },
  searchWrap: { position: "relative", width: "100%", maxWidth: "320px" },
  searchIcon: { position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" },
  searchInput: {
    width: "100%", boxSizing: "border-box", padding: "10px 16px 10px 40px", border: "1px solid #E5E7EB",
    borderRadius: "999px", fontSize: "14px", color: "#374151", background: "#F9FAFB", outline: "none",
  },
  exportGroup: { display: "flex", background: "#111827", borderRadius: "8px", overflow: "hidden" },
  exportBtn: {
    border: "none", background: "transparent", color: "#D1D5DB", fontSize: "14px", fontWeight: 500,
    padding: "8px 16px", cursor: "pointer", transition: "0.2s",
  },
  exportBtnHover: { background: "#1F2937", color: "#fff" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left" },
  theadRow: { background: "#F9FAFB" },
  th: {
    padding: "14px 24px", fontSize: "12px", fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.05em", color: "#6B7280", borderBottom: "1px solid #E5E7EB",
  },
  td: { padding: "16px 24px", fontSize: "14px", color: "#374151", borderBottom: "1px solid #F3F4F6" },
  rowHover: { background: "#F9FAFB" },
  emptyCell: { textAlign: "center", padding: "40px 24px", color: "#9CA3AF" },
  actionGroup: { display: "flex", gap: "12px" },
  editBtn: {
    display: "inline-flex", alignItems: "center", gap: "6px", border: "none", background: "none",
    padding: 0, fontSize: "14px", color: "#2563EB", cursor: "pointer", fontWeight: 500,
  },
  deleteBtn: {
    display: "inline-flex", alignItems: "center", gap: "6px", border: "none", background: "none",
    padding: 0, fontSize: "14px", color: "#EF4444", cursor: "pointer", fontWeight: 500,
  },
  footer: {
    display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
    gap: "12px", padding: "16px 24px", borderTop: "1px solid #F1F5F9",
  },
  resultsText: { margin: 0, fontSize: "14px", color: "#6B7280" },
  pagination: { display: "flex", alignItems: "center", gap: "6px" },
  pageNavBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", height: "36px", width: "36px",
    borderRadius: "8px", fontSize: "14px", fontWeight: 500, cursor: "pointer", border: "1px solid #E5E7EB",
    background: "#FFFFFF", color: "#6B7280",
  },
  pageNavBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  pageBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", height: "36px", minWidth: "36px",
    borderRadius: "8px", fontSize: "14px", fontWeight: 500, cursor: "pointer", border: "1px solid transparent",
    background: "none", color: "#6B7280",
  },
  pageBtnActive: { background: "#111827", color: "#FFFFFF", border: "1px solid #111827" },
  pageDots: { padding: "0 8px", fontSize: "14px", color: "#9CA3AF" },
};

export default function HourlyFareManagement() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(INITIAL_DUMMY_DATA);
  const [hoveredExportBtn, setHoveredExportBtn] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Modal states
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleOpenAdd = () => setAddModal(true);
  const handleCloseAdd = () => setAddModal(false);

  const handleOpenUpdate = (row) => {
    setSelectedRow(row);
    setUpdateModal(true);
  };
  const handleCloseUpdate = () => { setUpdateModal(false); setSelectedRow(null); };

  const handleOpenDelete = (row) => {
    setSelectedRow(row);
    setDeleteModal(true);
  };
  const handleCloseDelete = () => { setDeleteModal(false); setSelectedRow(null); };

  // CRUD Handlers
  const handleAdd = (newData) => {
    const newId = String(rows.length + 1).padStart(2, '0');
    setRows([...rows, { id: newId,...newData }]);
    handleCloseAdd();
  };

  const handleUpdate = (updatedData) => {
    setRows(rows.map(r => r.id === selectedRow.id? {...r,...updatedData } : r));
    handleCloseUpdate();
  };

  const handleDelete = () => {
    setRows(rows.filter(r => r.id!== selectedRow.id));
    handleCloseDelete();
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const paginatedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;
  const pageNumbers = getPageWindow(page, totalPages);
  const startItem = filteredRows.length === 0? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, filteredRows.length);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>Hourly wise Fare Management</h1>
            <button style={styles.addBtn} onClick={handleOpenAdd}>
              <Plus size={16} /> Add Fare Model
            </button>
          </div>
          <div style={styles.breadcrumb}>
            <span>Admin</span> / <span style={styles.breadcrumbActive}>Hourly Fare model</span>
          </div>
        </div>

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.searchWrap}>
            <Search style={styles.searchIcon} size={18} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              style={styles.searchInput}
            />
          </div>
          <div style={styles.exportGroup}>
            {EXPORT_ACTIONS.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => console.log(`Export via ${action}`)}
                onMouseEnter={() => setHoveredExportBtn(action)}
                onMouseLeave={() => setHoveredExportBtn(null)}
                style={{...styles.exportBtn,...(hoveredExportBtn === action? styles.exportBtnHover : {}) }}
              >
                {action}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.theadRow}>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Hours</th>
                <th style={styles.th}>Distance</th>
                <th style={styles.th}>Extra KM</th>
                <th style={styles.th}>Extra Amount</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0? (
                <tr><td colSpan={6} style={styles.emptyCell}>No results found.</td></tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={hoveredRow === row.id? styles.rowHover : {}}
                  >
                    <td style={{...styles.td, fontWeight: 500 }}>{row.id}</td>
                    <td style={styles.td}>{row.hours}</td>
                    <td style={styles.td}>{row.distance}</td>
                    <td style={styles.td}>{String(row.extraKm).padStart(2, "0")}</td>
                    <td style={styles.td}>{String(row.extraAmount).padStart(2, "0")}</td>
                    <td style={styles.td}>
                      <div style={styles.actionGroup}>
                        <button type="button" onClick={() => handleOpenUpdate(row)} style={styles.editBtn}>
                          <Pencil size={14} /> Edit
                        </button>
                        <button type="button" onClick={() => handleOpenDelete(row)} style={styles.deleteBtn}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.resultsText}>
            Showing {startItem} to {endItem} of {filteredRows.length} results
          </p>
          <div style={styles.pagination}>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              style={{...styles.pageNavBtn,...(page === 1? styles.pageNavBtnDisabled : {}) }}>
              <ChevronLeft size={18} />
            </button>
            {pageNumbers.map((n, i) =>
              n === "..."? <span key={`dots-${i}`} style={styles.pageDots}>...</span> : (
                <button key={n} type="button" onClick={() => setPage(n)}
                  style={{...styles.pageBtn,...(n === page? styles.pageBtnActive : {}) }}>{n}</button>
              )
            )}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{...styles.pageNavBtn,...(page === totalPages? styles.pageNavBtnDisabled : {}) }}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ServicetypeAddModal
        openDialog={addModal}
        handleClose={handleCloseAdd}
        onSubmit={handleAdd}
      />
      <ServicetypeUpdateModal
        openDialog={updateModal}
        handleClose={handleCloseUpdate}
        data={selectedRow}
        onSubmit={handleUpdate}
      />
      <ServicetypeDeleteDialog
        openDialog={deleteModal}
        handleClose={handleCloseDelete}
        data={selectedRow}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function getPageWindow(current, total, siblings = 1) {
  if (total <= 1) return [1];
  const pages = []; const start = Math.max(2, current - siblings); const end = Math.min(total - 1, current + siblings);
  pages.push(1); if (start > 2) pages.push("..."); for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("..."); if (total > 1) pages.push(total); return pages;
}